import axios from "axios";
import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button, Container } from "reactstrap";
import styled, { css, keyframes } from "styled-components";
import heroImage from "../../assets/img/hero-agent.jpeg";
import stagingRoom from "../../assets/img/hero-living-room.jpg";
import houseImage from "../../assets/img/house-exterior.jpg";
import REPORT_PREVIEW_2 from "../../assets/img/presentation.jpeg";
import AddressAutocomplete from "../../components/AddressAutocomplete/AddressAutocomplete";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { API_URL, ASSETS_BASE_URL, DEMO_REPORT_URL, DEMO_STAGING_URL } from "../../constants";
import { FREE_MONTHLY_CREDITS } from "../../constants/credits";
import { landingPage } from "../../constants/descriptions";
import { storageSet } from "../../utils/storage";

// Report preview images - stored in listing-iq-assets S3 bucket
const REPORT_PREVIEW_1 = `${ASSETS_BASE_URL}/demo_reports/preview-executive-summary.png`;
const REPORT_PREVIEW_3 = `${ASSETS_BASE_URL}/demo_reports/preview-recommendations.png`;

// "Another set of hands" tab -> preview screenshot
/** @type {Record<string, string>} */
const HANDS_PREVIEWS = {
  research: REPORT_PREVIEW_1,
  recommend: REPORT_PREVIEW_3,
  present: REPORT_PREVIEW_2,
};

const HeroSection = styled.section`
  padding: 88px 0 52px;
@media (max-width: 767px) {
    padding: 72px 0 44px;
  }

  background: linear-gradient(
    180deg,
    var(--brand-bg) 0%,
    var(--brand-bg-soft) 100%
  );
  border-bottom: 1px solid var(--brand-border);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 300px;
    height: 300px;
    background: radial-gradient(
      circle 260px at calc(100% - 20px) 20px,
      rgba(14, 126, 122, 0.12),
      transparent 70%
    );
    pointer-events: none;
  }
`;

const HeroPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--brand-primary-50);
  color: var(--brand-primary-700);
  font-weight: 600;
  font-size: 12.5px;
  padding: 6px 12px;
  border-radius: 999px;
  margin-bottom: 20px;
  border: 1px solid var(--brand-primary-100);

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    background: var(--brand-primary);
    border-radius: 50%;
    box-shadow: 0 0 0 3px rgba(14, 126, 122, 0.18);
  }
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  gap: 60px;

  @media (max-width: 991px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const HeroText = styled.div`
  flex: 1;
`;

const HeroTitle = styled.h1`
  font-size: 52px;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: var(--brand-ink);
  margin-bottom: 20px;

  @media (max-width: 767px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.55;
  color: var(--brand-ink-3);
  margin-bottom: 32px;
  max-width: 52ch;
`;

const HeroButton = styled(Button)`
  background: var(--brand-primary);
  border: none;
  border-radius: var(--brand-radius-lg);
  padding: 14px 28px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  box-shadow: var(--brand-shadow-primary);
  transition: background 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    background: var(--brand-primary-600);
    transform: translateY(-1px);
    box-shadow: var(--brand-shadow-primary-hover);
  }

  &:focus,
  &:active {
    background: var(--brand-primary-700) !important;
    box-shadow: var(--brand-shadow-primary) !important;
  }
`;

const HeroImageContainer = styled.div`
  flex: 1;
  position: relative;

  img {
    width: 100%;
    height: auto;
    border-radius: var(--brand-radius-xl);
    border: 1px solid var(--brand-border);
    box-shadow: var(--brand-shadow-lg);
  }
`;

const FeaturesSection = styled.section`
  padding: 60px 0;
  background: var(--brand-bg-soft);
  border-top: 1px solid var(--brand-border);
  border-bottom: 1px solid var(--brand-border);
`;

const SectionTitle = styled.h2`
  font-size: 38px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.028em;
  text-align: center;
  color: var(--brand-ink);
  margin-bottom: 40px;

  @media (max-width: 767px) {
    font-size: 28px;
  }
`;

/** @param {{ name: string }} props */
const StepGlyph = ({ name }) => {
  /** @type {import("react").SVGProps<SVGSVGElement>} */
  const p = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  if (name === "address")
    return (
      <svg {...p} aria-hidden="true">
        <path d="M12 21s-6-5.6-6-10a6 6 0 1 1 12 0c0 4.4-6 10-6 10z" />
        <circle cx="12" cy="11" r="2.4" />
      </svg>
    );
  if (name === "share")
    return (
      <svg {...p} aria-hidden="true">
        <path d="M4 13v6a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-6" />
        <path d="M12 3v13" />
        <path d="M8 7l4-4 4 4" />
      </svg>
    );
  if (name === "spark")
    return (
      <svg {...p} aria-hidden="true">
        <path d="M12 2.5l1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.9z" />
        <path d="M18.5 14l.85 2.55L22 17.5l-2.65.95L18.5 21l-.85-2.55L15 17.5l2.65-.95z" />
      </svg>
    );
  return null;
};

const ConnectorArrow = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

const HowItWorksSection = styled.section`
  padding: 72px 0 64px;
  background: var(--brand-bg);
  @media (max-width: 767px) {
    padding: 52px 0;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  align-items: stretch;
  gap: 16px;
  margin-top: 36px;

  @media (max-width: 991px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    margin-top: 28px;
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.09);
    opacity: 0.82;
  }
`;

const StepCard = styled.div`
  flex: 1;
  position: relative;
  border: 1px solid var(--brand-border);
  border-radius: var(--brand-radius-xl);
  padding: 30px 26px;
  text-align: left;
  background: #fff;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s cubic-bezier(0.3, 1, 0.3, 1),
    box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: var(--brand-primary-100);
    box-shadow: var(--brand-shadow);
  }

  ${(p) =>
    p.$auto &&
    css`
      background: linear-gradient(
        160deg,
        var(--brand-primary) 0%,
        var(--brand-primary-600) 100%
      );
      border-color: transparent;
      box-shadow: var(--brand-shadow-primary);

      &:hover {
        border-color: transparent;
        box-shadow: var(--brand-shadow-primary-hover);
      }
    `}
`;

const AutoTag = styled.span`
  position: absolute;
  top: 18px;
  right: 18px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 4px 11px 4px 9px;
  border-radius: 999px;

  &::before {
    content: "\u2726";
    font-size: 10px;
  }
`;

const StepIcon = styled.div`
  color: var(--brand-primary);
  margin-bottom: 16px;
  line-height: 0;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const AutoIcon = styled.div`
  color: #fff;
  margin-bottom: 16px;
  line-height: 0;
  transform-origin: left center;
  animation: ${pulseGlow} 2.6s ease-in-out infinite;

  svg {
    width: 40px;
    height: 40px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StepTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: ${(p) => (p.$auto ? "#fff" : "var(--brand-ink)")};
  margin-bottom: 10px;
`;

const StepDescription = styled.p`
  font-size: 15px;
  line-height: 1.55;
  color: ${(p) => (p.$auto ? "rgba(255, 255, 255, 0.88)" : "var(--brand-ink-3)")};
  margin: 0;
`;

const StepConnector = styled.div`
  align-self: center;
  flex: 0 0 auto;
  color: var(--brand-ink-4);
  display: flex;
  align-items: center;
  line-height: 0;

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 991px) {
    transform: rotate(90deg);
    margin: 2px 0;
  }
`;

const DataBackedSection = styled.section`
  padding: 72px 0;
  background: var(--brand-bg-soft);
  border-top: 1px solid var(--brand-border);

  @media (max-width: 767px) {
    padding: 52px 0;
  }
`;

const DataBackedContent = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  align-items: center;
  gap: 72px;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const DataBackedText = styled.div`
  flex: 1;
`;

const DataBackedTitle = styled.h2`
  font-size: 38px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.028em;
  color: var(--brand-ink);
  margin-bottom: 20px;

  @media (max-width: 767px) {
    font-size: 28px;
  }
`;

const DataBackedSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.55;
  color: var(--brand-ink-3);
  margin-bottom: 24px;
`;

const HeroFreeSignal = styled.p`
  margin: 12px 0 0;
  font-size: 14.5px;
  color: var(--brand-ink-3, #5c7074);
`;

const Guarantee = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  margin: 32px auto 0;
  font-size: 16.5px;
  color: var(--brand-ink-2, #2b3f42);

  svg {
    color: var(--brand-primary, #0e7e7a);
    flex-shrink: 0;
  }

  strong {
    font-weight: 700;
    color: var(--brand-ink, #0b1c1e);
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 24px;
`;

const BenefitItem = styled.li`
  padding-left: 34px;
  position: relative;
  margin-bottom: 18px;

  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    top: 2px;
    width: 22px;
    height: 22px;
    background: var(--brand-primary-50);
    color: var(--brand-primary-700);
    border-radius: 999px;
    font-weight: 700;
    font-size: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const BenefitTitle = styled.span`
  display: block;
  font-size: 16.5px;
  line-height: 1.4;
  font-weight: 700;
  color: var(--brand-ink);
  margin-bottom: 3px;
`;

const BenefitDescription = styled.span`
  display: block;
  font-size: 15px;
  line-height: 1.55;
  color: var(--brand-ink-3);
`;

const DataBackedImageContainer = styled.div`
  flex: 1;

  img {
    width: 100%;
    height: auto;
    border-radius: var(--brand-radius-xl);
    border: 1px solid var(--brand-border);
    box-shadow: var(--brand-shadow-lg);
  }
`;

/* ===== "See the possibilities" section (staging + photo touch-ups) ===== */
const PossibilitiesSection = styled.section`
  padding: 60px 0;
 @media (max-width: 767px) {
    padding: 56px 0;
  }
  background: linear-gradient(
    180deg,
    var(--brand-bg) 0%,
    var(--brand-primary-50) 100%
  );
  border-top: 1px solid var(--brand-border);
  border-bottom: 1px solid var(--brand-border);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -200px;
    left: -200px;
    width: 480px;
    height: 480px;
    background: radial-gradient(
      circle,
      rgba(14, 126, 122, 0.1),
      transparent 70%
    );
    pointer-events: none;
  }
`;

const SectionHead = styled.div`
  text-align: center;
  max-width: 660px;
  margin: 0 auto 42px;
  @media (max-width: 767px) {
    margin-bottom: 34px;
  }
`;

const PossibilitiesTitle = styled.h2`
  font-size: clamp(32px, 4vw, 42px);
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: -0.028em;
  color: var(--brand-ink);
  margin: 0 0 16px;

  em {
    font-style: normal;
    color: var(--brand-primary);
  }
`;

const FeatureRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 42px;
  align-items: center;

  & + & {
    margin-top: 52px;
  }

  /* zigzag: pull the media to the left for reversed rows (desktop only) */
  @media (min-width: 861px) {
    &[data-reverse="true"] .feature-media {
      order: -1;
    }
  }

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 28px;

    & + & {
      margin-top: 44px;
    }
  }
`;

const FeatureMedia = styled.div`
  min-width: 0;
`;

const FeatureBody = styled.div`
  min-width: 0;
`;

const FeatureLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--brand-primary-50);
  color: var(--brand-primary-700);
  font-weight: 600;
  font-size: 12.5px;
  padding: 5px 13px;
  border-radius: 999px;
  margin-bottom: 14px;

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--brand-primary);
  }
`;

const FeatureTitle = styled.h3`
  font-size: clamp(22px, 2.6vw, 28px);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: var(--brand-ink);
  margin: 0 0 12px;
`;

const FeatureDesc = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: var(--brand-ink-3);
  margin: 0 0 18px;
  max-width: 46ch;
`;

const RowCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--brand-primary);
  color: #fff;
  font-weight: 600;
  font-size: 15px;
  padding: 13px 22px;
  border-radius: var(--brand-radius-lg);
  text-decoration: none;
  box-shadow: var(--brand-shadow-primary);
  transition: background 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    background: var(--brand-primary-600);
    color: #fff;
    transform: translateY(-1px);
    box-shadow: var(--brand-shadow-primary-hover);
    text-decoration: none;
  }

  &::after {
    content: "\u2192";
    transition: transform 0.2s cubic-bezier(0.3, 1, 0.3, 1);
  }

  &:hover::after {
    transform: translateX(3px);
  }
`;

/* --- Staging: annotated room --- */
const AnnotatedRoom = styled.div`
  position: relative;
  border-radius: var(--brand-radius-lg);
  overflow: hidden;
  border: 1px solid var(--brand-border);
  box-shadow: var(--brand-shadow);
  aspect-ratio: 4 / 3;
  background: var(--brand-bg);
`;

const RoomImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const RoomScrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(15, 32, 30, 0.04) 0%,
    rgba(15, 32, 30, 0.16) 100%
  );
  pointer-events: none;
`;

/** @type {import('styled-components').StyledComponent<'span', any, {}, never>} */
const Pin = styled.span`
  position: absolute;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.96);
  color: var(--brand-ink);
  font-size: 12.5px;
  font-weight: 600;
  padding: 5px 12px 5px 10px;
  border-radius: 999px;
  border: 1px solid var(--brand-border);
  box-shadow: var(--brand-shadow);
  white-space: nowrap;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--brand-primary);
    box-shadow: 0 0 0 4px rgba(14, 126, 122, 0.18);
  }

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 4px 9px 4px 8px;
  }
`;

/* --- Photo touch-ups: before / after (reused) --- */
const TouchupSample = styled.figure`
  background: #fff;
  border: 1px solid var(--brand-border);
  border-radius: var(--brand-radius-lg);
  padding: 20px;
  margin: 0;
  box-shadow: var(--brand-shadow);
`;

const TouchupSampleCaption = styled.figcaption`
  font-size: 14px;
  font-weight: 600;
  color: var(--brand-ink-2);
  margin: 0 0 14px;
  text-align: center;
  letter-spacing: -0.005em;
`;

const TouchupSamplePair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TouchupSampleFrame = styled.button`
  position: relative;
  width: 100%;
  appearance: none;
  padding: 0;
  color: inherit;
  font: inherit;
  text-align: inherit;
  border-radius: var(--brand-radius);
  overflow: hidden;
  border: 1px solid var(--brand-border);
  background: var(--brand-bg);
  aspect-ratio: 4 / 3;
`;

const TouchupSampleImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  cursor: zoom-in;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

/** @type {import('styled-components').StyledComponent<'span', any, { $variant?: string }, never>} */
const TouchupSampleLabel = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(255, 255, 255, 0.95);
  color: ${(/** @type {{ $variant?: string }} */ p) => (p.$variant === "after" ? "var(--brand-primary-700)" : "var(--brand-ink-2)")};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid var(--brand-border);
`;

const HowItWorksCta = styled.section`
  padding: 44px 0 6px;
  text-align: center;
`;

const CtaAddressBar = styled.div`
  display: flex;
  gap: 10px;
  max-width: 540px;
  margin: 0 auto;
  align-items: stretch;

  /* the autocomplete wrapper fills the row; the green button anchors it */
  & > *:first-child {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 560px) {
    flex-direction: column;
  }
`;

const HandsSection = styled.section`
  background: var(--brand-bg);

  @media (max-width: 860px) {
    padding: 72px 0;
  }
`;

/* Desktop: a tall track supplies the scroll distance; a full-viewport stage
   pins (position: sticky, height: 100vh) so the headline + panel freeze
   together and the whole screen is filled (no empty band). padding-top clears
   the fixed navbar; the active tab advances with scroll progress. */
const HandsTrack = styled.div`
  position: relative;
  height: 140vh;

  @media (max-width: 860px) {
    display: none;
  }
`;

const HandsStage = styled.div`
  position: sticky;
  top: 64px;
  padding: 38px 0 15px;
`;

const HandsInner = styled(Container)`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const HandsLayout = styled.div`
  flex: 1;
  min-height: 0;
  margin-top: 0px;
  display: grid;
  grid-template-columns: 0.82fr 1.18fr;
  gap: 44px;
  align-items: stretch;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const HandsTabs = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 10px;
`;

const HandsTab = styled.button`
  appearance: none;
  text-align: left;
  background: ${(p) => (p.$active ? "var(--brand-primary-50)" : "transparent")};
  border: none;
  border-left: 3px solid
    ${(p) => (p.$active ? "var(--brand-primary)" : "transparent")};
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: ${(p) => (p.$active ? "var(--brand-primary-50)" : "var(--brand-bg-soft)")};
  }
`;

const HandsTabName = styled.h3`
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: ${(p) => (p.$active ? "var(--brand-primary-700)" : "var(--brand-ink-2)")};
  margin: 0 0 4px;
`;

const HandsTabBlurb = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: ${(p) => (p.$active ? "var(--brand-ink-3)" : "var(--brand-ink-4)")};
  margin: 0;
`;

const HandsPreviewCol = styled.div`
  min-width: 0;
  min-height: 0;
  display: flex;
  align-items: stretch;

`;

const HandsFrame = styled.button`
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 4;
  max-height: 62vh;
  appearance: none;
  padding: 0;
  color: inherit;
  font: inherit;
  text-align: inherit;

  border-radius: var(--brand-radius-xl);
  overflow: hidden;
  border: 1px solid var(--brand-border);
  box-shadow: var(--brand-shadow);
  background: var(--brand-bg-softer);
  cursor: zoom-in;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    box-shadow: var(--brand-shadow-lg);
    border-color: var(--brand-primary-100);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    padding: 12px;
    box-sizing: border-box;
    display: block;
  }
`;

/* Mobile: no pinning — stack each deliverable with its own preview so all
   three are seen by scrolling normally. */
const HandsMobile = styled.div`
  display: none;

  @media (max-width: 860px) {
    display: block;
    margin-top: 28px;
  }
`;

const HandsMobileItem = styled.div`
  margin-bottom: 30px;

  ${HandsFrame} {
    flex: none;
    aspect-ratio: 4 / 3;
    margin-top: 14px;
  }
`;

const ViewFullReportButton = styled.a`
  display: inline-block;
  margin-top: 28px;
  background: #fff;
  border: 1px solid var(--brand-border-strong);
  border-radius: var(--brand-radius-lg);
  padding: 13px 22px;
  font-size: 15px;
  font-weight: 600;
  color: var(--brand-ink);
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--brand-bg-soft);
    border-color: var(--brand-ink-4);
    color: var(--brand-ink);
    text-decoration: none;
  }
`;

/* ===== Listing journey (interactive timeline) ===== */
const JourneyHeadline = styled.h2`
  font-family: var(--font-heading);
  text-align: center;
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.18;
  color: var(--brand-ink);
  margin: 0 0 14px;
`;

const JourneyStem = styled.span`
  display: block;
`;

const JourneyRotSlot = styled.span`
  display: block;
  overflow: hidden;
  height: 1.4em;
  color: var(--brand-primary);
`;

const journeyRollIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const JourneyRotInner = styled.span`
  display: block;
  line-height: 1.4;
  animation: ${journeyRollIn} 0.45s cubic-bezier(0.4, 0, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const JourneyBody = styled.div`
  margin-top: 4px;
`;

const JourneySegWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 34px;
`;

const JourneySeg = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  background: var(--brand-bg-soft);
  border: 1px solid var(--brand-border);
  border-radius: 14px;
  padding: 6px;
  max-width: 100%;
`;

const JourneySegBtn = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--brand-ink-3);
  padding: 9px 16px;
  border-radius: 10px;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    color: var(--brand-ink);
  }

  &[aria-selected="true"] {
    background: var(--brand-primary);
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid var(--brand-primary);
    outline-offset: 2px;
  }
`;

const journeyFade = keyframes`
  from {
    opacity: 0.35;
  }
  to {
    opacity: 1;
  }
`;

const JourneyPanel = styled.div`
  max-width: 640px;
  margin: 0 auto;
  text-align: center;
  animation: ${journeyFade} 0.35s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const JourneyOutputs = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  padding: 0;
  margin: 0 0 18px;
`;

const JourneyOutput = styled.li`
  font-size: 14px;
  font-weight: 600;
  color: var(--brand-ink-2, #2a2a2a);
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 8px 14px;

  &::before {
    content: "✓";
    color: var(--brand-primary, #0e7e7a);
    font-weight: 700;
    margin-right: 8px;
  }
`;

const JourneyOutcome = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: var(--brand-ink-3, #4a4a4a);
  margin: 0 auto;
  max-width: 560px;
`;

const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 40px;
  cursor: pointer;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const LightboxImage = styled.img`
  max-width: 90%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
  cursor: default;
  animation: scaleIn 0.25s ease-out;

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const LightboxCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  color: #1a1a1a;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    transform: scale(1.1);
  }
`;

const HeroAddressBox = styled.div`
  margin-top: 22px;
  max-width: 480px;
`;

const HeroAddressStatus = styled.div`
  margin-top: 8px;
  font-size: 0.9rem;
  color: #6c757d;
`;

const NewLandingPage = () => {
  const history = useHistory();
  const [lightboxImage, setLightboxImage] = useState(/** @type {string | null} */ (null));
  const [addressBusy, setAddressBusy] = useState(false);
  const addressLookupGenerationRef = useRef(0);
  const addressLookupInFlightRef = useRef(false);

  useEffect(
    () => () => {
      addressLookupGenerationRef.current += 1;
      addressLookupInFlightRef.current = false;
    },
    []
  );

  /**
   * Hero address entry: look the property up and drop the visitor into the
   * right flow with everything pre-filled — active listing → Zillow import
   * with the URL ready; anything else → manual pre-listing form pre-filled
   * from the most recent Zillow record.
   * @param {{display: string, zpid: number, city: string, state: string, zipcode: string}} suggestion
   */
  const handleHeroAddress = (suggestion) => {
    if (addressLookupInFlightRef.current) return;
    addressLookupInFlightRef.current = true;
    const generation = ++addressLookupGenerationRef.current;
    setAddressBusy(true);
    axios
      .get(`${API_URL}/address-lookup/`, {
        params: { address: suggestion.display },
        timeout: 15000,
      })
      .then(({ data }) => {
        if (generation !== addressLookupGenerationRef.current) return;
        const property = data.property || {};
        if (property.home_status === "FOR_SALE" && property.zillow_url) {
          storageSet("pendingZillowUrl", property.zillow_url);
          history.push("/verify");
        } else {
          storageSet("pendingAddressPrefill", JSON.stringify({ property, suggestion }));
          history.push("/submit");
        }
      })
      .catch(() => {
        if (generation !== addressLookupGenerationRef.current) return;
        storageSet("pendingAddressPrefill", JSON.stringify({ property: null, suggestion }));
        history.push("/submit");
      })
      .finally(() => {
        if (generation === addressLookupGenerationRef.current) {
          addressLookupInFlightRef.current = false;
          setAddressBusy(false);
        }
      });
  };

  /** @param {string} address */
  const handleHeroFreeText = (address) => {
    if (addressLookupInFlightRef.current) return;
    addressLookupInFlightRef.current = true;
    setAddressBusy(true);
    storageSet(
      "pendingAddressPrefill",
      JSON.stringify({
        property: null,
        suggestion: { display: address, city: "", state: "", zipcode: "" },
      })
    );
    history.push("/submit");
  };

  /** @param {string} url */
  const handleHeroListingUrl = (url) => {
    if (addressLookupInFlightRef.current) return;
    addressLookupInFlightRef.current = true;
    setAddressBusy(true);
    storageSet("pendingZillowUrl", url);
    history.push("/verify");
  };

  const goToSubmit = () => {
    history.push("/verify");
  };

  /** @param {string} imageSrc */
  const openLightbox = (imageSrc) => {
    setLightboxImage(imageSrc);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const staging = landingPage.touchups.staging;
  const photoTouchups = landingPage.touchups.photoTouchups;
  const featuredTouchup = photoTouchups.samples[0];
  const stagingSampleUrl = staging.sampleUrl || DEMO_STAGING_URL;

  const journey = landingPage.listingJourney;
  const [activeStep, setActiveStep] = useState(0);
  const step = journey.milestones[activeStep];
  const journeyRef = useRef(null);
  const journeyInView = useRef(false);
  const journeyHover = useRef(false);
  const journeyEngaged = useRef(false);
  const segRefs = useRef(/** @type {(HTMLButtonElement | null)[]} */ ([]));

  useEffect(() => {
    const el = journeyRef.current;
    if (!el || typeof IntersectionObserver === "undefined" || !window.matchMedia) {
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        journeyInView.current = entries[0].isIntersecting;
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let timer = 0;
    if (!reduce) {
      timer = window.setInterval(() => {
        if (journeyInView.current && !journeyHover.current && !journeyEngaged.current) {
          setActiveStep((prev) => (prev + 1) % journey.milestones.length);
        }
      }, 2500);
    }
    return () => {
      io.disconnect();
      if (timer) window.clearInterval(timer);
    };
  }, [journey.milestones.length]);

  /** @param {number} i */
  const selectStep = (i) => {
    journeyEngaged.current = true;
    setActiveStep(i);
  };

  /** @param {import("react").KeyboardEvent} e */
  const onJourneyKeyDown = (e) => {
    const count = journey.milestones.length;
    /** @type {number | null} */
    let next = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      next = (activeStep + 1) % count;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      next = (activeStep - 1 + count) % count;
    } else if (e.key === "Home") {
      next = 0;
    } else if (e.key === "End") {
      next = count - 1;
    }
    if (next !== null) {
      e.preventDefault();
      journeyEngaged.current = true;
      setActiveStep(next);
      const node = segRefs.current[next];
      if (node) node.focus();
    }
  };

  const handsTabs = landingPage.anotherSetOfHands.tabs;
  const [activeHand, setActiveHand] = useState(0);
  const handsTrackRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  useEffect(() => {
    const track = handsTrackRef.current;
    if (!track || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(min-width: 861px)");
    let raf = 0;
    const update = () => {
      raf = 0;
      if (!mq.matches) return;
      const rect = track.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      if (total <= 0) return;
      const progress = Math.min(Math.max(-rect.top / total, 0), 1);
      let idx = Math.floor(progress * handsTabs.length);
      if (idx >= handsTabs.length) idx = handsTabs.length - 1;
      if (idx < 0) idx = 0;
      setActiveHand(idx);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [handsTabs.length]);

  /** @param {number} i */
  const scrollToHand = (i) => {
    const track = handsTrackRef.current;
    if (!track) return;
    const total = track.offsetHeight - window.innerHeight;
    if (total <= 0) return;
    const top =
      window.scrollY + track.getBoundingClientRect().top + (total * (i + 0.5)) / handsTabs.length;
    const reduce = window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : true;
    window.scrollTo({ top, behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <>
      <NewNavbar />

      {/* Hero Section */}
      <HeroSection>
        <Container>
          <HeroContent>
            <HeroText>
              <HeroPill>{landingPage.header.pill}</HeroPill>
              <HeroTitle>{landingPage.header.title}</HeroTitle>
              <HeroSubtitle>
                {landingPage.header.subtitle1} {landingPage.header.subtitle2}
              </HeroSubtitle>
              <HeroAddressBox>
                <AddressAutocomplete
                  id="heroAddress"
                  placeholder="Enter an address or Zillow listing link…"
                  onSelect={handleHeroAddress}
                  onFreeText={handleHeroFreeText}
                  onListingUrl={handleHeroListingUrl}
                  continueLabel="Continue"
                  disabled={addressBusy}
                />
                {addressBusy && (
                  <HeroAddressStatus as="output" aria-live="polite">
                    Looking up the property…
                  </HeroAddressStatus>
                )}
              </HeroAddressBox>
              <HeroFreeSignal>{landingPage.header.freeSignal}</HeroFreeSignal>
            </HeroText>
            <HeroImageContainer>
              <img src={heroImage} alt="Modern living room" />
            </HeroImageContainer>
          </HeroContent>
        </Container>
      </HeroSection>

      {/* Report Preview Section */}
      <HandsSection>
        {/* Desktop: full-viewport stage pins the headline + panel together;
            tabs advance as you scroll the track */}
        <HandsTrack ref={handsTrackRef}>
          <HandsStage>
            <HandsInner>
              <SectionTitle>{landingPage.anotherSetOfHands.title}</SectionTitle>
              <HandsLayout>
                <HandsTabs role="tablist" aria-label="Sample sections">
                  {handsTabs.map((tab, i) => (
                    <HandsTab
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={i === activeHand}
                      $active={i === activeHand}
                      onClick={() => scrollToHand(i)}
                    >
                      <HandsTabName $active={i === activeHand}>{tab.label}</HandsTabName>
                      <HandsTabBlurb $active={i === activeHand}>{tab.blurb}</HandsTabBlurb>
                    </HandsTab>
                  ))}
                  <ViewFullReportButton
                    href={DEMO_REPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {landingPage.anotherSetOfHands.cta}
                  </ViewFullReportButton>
                </HandsTabs>
                <HandsPreviewCol>
                  <HandsFrame
                    type="button"
                    onClick={() => openLightbox(HANDS_PREVIEWS[handsTabs[activeHand].key])}
                  >
                    <img
                      key={handsTabs[activeHand].key}
                      src={HANDS_PREVIEWS[handsTabs[activeHand].key]}
                      alt={`${handsTabs[activeHand].label} sample`}
                    />
                  </HandsFrame>
                </HandsPreviewCol>
              </HandsLayout>
            </HandsInner>
          </HandsStage>
        </HandsTrack>

        {/* Mobile: stacked so all three are seen while scrolling */}
        <HandsMobile>
          <Container>
            <SectionTitle>{landingPage.anotherSetOfHands.title}</SectionTitle>
            {handsTabs.map((tab) => (
              <HandsMobileItem key={tab.key}>
                <HandsTabName $active>{tab.label}</HandsTabName>
                <HandsTabBlurb $active>{tab.blurb}</HandsTabBlurb>
                <HandsFrame
                  type="button"
                  onClick={() => openLightbox(HANDS_PREVIEWS[tab.key])}
                  style={{ marginTop: "14px" }}
                >
                  <img
                    src={HANDS_PREVIEWS[tab.key]}
                    alt={`${tab.label} sample`}
                    width="800"
                    height="600"
                    loading="lazy"
                  />
                </HandsFrame>
              </HandsMobileItem>
            ))}
            <div style={{ textAlign: "center" }}>
              <ViewFullReportButton
                href={DEMO_REPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {landingPage.anotherSetOfHands.cta}
              </ViewFullReportButton>
            </div>
          </Container>
        </HandsMobile>
      </HandsSection>
      {/* Listing journey */}
      <FeaturesSection>
        <Container>
          <JourneyBody
            ref={journeyRef}
            onMouseEnter={() => {
              journeyHover.current = true;
            }}
            onMouseLeave={() => {
              journeyHover.current = false;
            }}
          >
            <JourneyHeadline>
              <JourneyStem>{journey.stem}</JourneyStem>
              <JourneyRotSlot>
                <JourneyRotInner key={activeStep}>{step.phrase}</JourneyRotInner>
              </JourneyRotSlot>
            </JourneyHeadline>
            <JourneySegWrap>
              <JourneySeg role="tablist" aria-label="Listing moments">
                {journey.milestones.map((m, i) => (
                  <JourneySegBtn
                    key={m.key}
                    ref={(el) => {
                      segRefs.current[i] = el;
                    }}
                    id={`journey-tab-${m.key}`}
                    type="button"
                    role="tab"
                    aria-selected={i === activeStep}
                    aria-controls="journey-panel"
                    tabIndex={i === activeStep ? 0 : -1}
                    onClick={() => selectStep(i)}
                    onKeyDown={onJourneyKeyDown}
                  >
                    {m.label}
                  </JourneySegBtn>
                ))}
              </JourneySeg>
            </JourneySegWrap>

            <JourneyPanel
              key={activeStep}
              id="journey-panel"
              role="tabpanel"
              aria-labelledby={`journey-tab-${step.key}`}
            >
              <JourneyOutputs>
                {(step.outputs || []).map((output) => (
                  <JourneyOutput key={output}>{output}</JourneyOutput>
                ))}
              </JourneyOutputs>
              {step.outcome && <JourneyOutcome>{step.outcome}</JourneyOutcome>}
            </JourneyPanel>
          </JourneyBody>
        </Container>
      </FeaturesSection>

      {/* How It Works Section */}
      <HowItWorksSection>
        <Container>
          <SectionTitle>{landingPage.howItWorks.title}</SectionTitle>
          <StepsContainer>
            {landingPage.howItWorks.steps.map((step, index) => (
              <Fragment key={step.number}>
                <StepCard $auto={step.automatic}>
                  {step.automatic ? (
                    <>
                      <AutoTag>Automatic</AutoTag>
                      <AutoIcon aria-hidden="true">
                        <StepGlyph name="spark" />
                      </AutoIcon>
                    </>
                  ) : (
                    <>
                      <StepIcon aria-hidden="true">
                        <StepGlyph name={step.icon} />
                      </StepIcon>
                    </>
                  )}
                  <StepTitle $auto={step.automatic}>{step.title}</StepTitle>
                  <StepDescription $auto={step.automatic}>{step.description}</StepDescription>
                </StepCard>
                {index < landingPage.howItWorks.steps.length - 1 && (
                  <StepConnector aria-hidden="true">
                    <ConnectorArrow />
                  </StepConnector>
                )}
              </Fragment>
            ))}
          </StepsContainer>
          <HowItWorksCta>
            <CtaAddressBar>
              <AddressAutocomplete
                id="howItWorksAddress"
                placeholder="Enter an address or Zillow listing link…"
                onSelect={handleHeroAddress}
                onFreeText={handleHeroFreeText}
                onListingUrl={handleHeroListingUrl}
                continueLabel={landingPage.howItWorks.button}
                disabled={addressBusy}
              />
            </CtaAddressBar>
            {addressBusy && (
              <HeroAddressStatus as="output" aria-live="polite">
                Looking up the property…
              </HeroAddressStatus>
            )}
          </HowItWorksCta>
        </Container>
      </HowItWorksSection>

      {/* See the possibilities — staging recommendations + photo touch-ups */}
      <PossibilitiesSection>
        <Container>
          <SectionHead>
            <PossibilitiesTitle>{landingPage.touchups.title}</PossibilitiesTitle>
          </SectionHead>

          {/* Row 1 — Staging recommendations (text left, annotated room right) */}
          <FeatureRow>
            <FeatureBody>
              <FeatureLabel>{staging.label}</FeatureLabel>
              <FeatureTitle>{staging.title}</FeatureTitle>
              <FeatureDesc>{staging.description}</FeatureDesc>

              <RowCTA href={stagingSampleUrl} target="_blank" rel="noopener noreferrer">
                {staging.cta}
              </RowCTA>
            </FeatureBody>
            <FeatureMedia className="feature-media">
              <AnnotatedRoom>
                <RoomImg
                  src={stagingRoom}
                  alt="Living room with staging recommendations"
                  width="800"
                  height="600"
                  loading="lazy"
                />
                <RoomScrim />
                {staging.annotations.map((note) => (
                  <Pin key={note.label} style={{ top: note.top, left: note.left }}>
                    {note.label}
                  </Pin>
                ))}
              </AnnotatedRoom>
            </FeatureMedia>
          </FeatureRow>

          {/* Row 2 — AI photo touch-ups (media left on desktop, text right) */}
          <FeatureRow data-reverse="true">
            <FeatureBody>
              <FeatureLabel>{photoTouchups.label}</FeatureLabel>
              <FeatureTitle>{photoTouchups.title}</FeatureTitle>
              <FeatureDesc>{photoTouchups.description}</FeatureDesc>

              <RowCTA as={Link} to="/listing-photos-and-video">
                {photoTouchups.cta}
              </RowCTA>
            </FeatureBody>
            <FeatureMedia className="feature-media">
              <TouchupSample>
                <TouchupSampleCaption>{featuredTouchup.caption}</TouchupSampleCaption>
                <TouchupSamplePair>
                  <TouchupSampleFrame
                    type="button"
                    onClick={() => openLightbox(featuredTouchup.originalUrl)}
                  >
                    <TouchupSampleImg
                      src={featuredTouchup.originalUrl}
                      alt={`${featuredTouchup.caption} — before`}
                      width="800"
                      height="600"
                      loading="lazy"
                    />
                    <TouchupSampleLabel>Before</TouchupSampleLabel>
                  </TouchupSampleFrame>
                  <TouchupSampleFrame
                    type="button"
                    onClick={() => openLightbox(featuredTouchup.editedUrl)}
                  >
                    <TouchupSampleImg
                      src={featuredTouchup.editedUrl}
                      alt={`${featuredTouchup.caption} — after`}
                      width="800"
                      height="600"
                      loading="lazy"
                    />
                    <TouchupSampleLabel $variant="after">After</TouchupSampleLabel>
                  </TouchupSampleFrame>
                </TouchupSamplePair>
              </TouchupSample>
            </FeatureMedia>
          </FeatureRow>
        </Container>
      </PossibilitiesSection>

      {/* Data-Backed Insights Section */}
      <DataBackedSection>
        <Container>
          <DataBackedContent>
            <DataBackedText>
              <DataBackedTitle>{landingPage.dataBackedSection.title}</DataBackedTitle>
              <DataBackedSubtitle>{landingPage.dataBackedSection.subtitle}</DataBackedSubtitle>
              <BenefitsList>
                {landingPage.dataBackedSection.benefits.map((benefit) => (
                  <BenefitItem key={benefit.title}>
                    <BenefitTitle>{benefit.title}</BenefitTitle>
                    <BenefitDescription>{benefit.description}</BenefitDescription>
                  </BenefitItem>
                ))}
              </BenefitsList>
              <HeroButton onClick={goToSubmit} size="lg">
                {landingPage.dataBackedSection.button}
              </HeroButton>
              <Guarantee>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                <span>
                  <strong>
                    Start free — {FREE_MONTHLY_CREDITS.toLocaleString("en-US")} credits a month, no
                    card required.
                  </strong>{" "}
                  Full refund within 7 days of any plan or pack purchase.
                </span>
              </Guarantee>
            </DataBackedText>
            <DataBackedImageContainer>
              <img src={houseImage} alt="Beautiful house exterior" />
            </DataBackedImageContainer>
          </DataBackedContent>
        </Container>
      </DataBackedSection>

      <NewFooter />

      {/* Lightbox Modal */}
      {lightboxImage && (
        // biome-ignore lint/a11y/useSemanticElements: conditionally rendered styled overlay provides modal presentation
        <LightboxOverlay role="dialog" aria-modal="true" onClick={closeLightbox}>
          <LightboxCloseButton type="button" aria-label="Close preview" onClick={closeLightbox}>
            ×
          </LightboxCloseButton>
          <LightboxImage
            src={lightboxImage}
            alt="Preview enlarged"
            onClick={(e) => e.stopPropagation()}
          />
        </LightboxOverlay>
      )}
    </>
  );
};

export default NewLandingPage;
